import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Shield, ShieldOff, Ban, Check, Trash2, UserPlus, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { NewUserDialog, ResetPasswordDialog } from "./UserCredentialDialogs";

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [resetUser, setResetUser] = useState(null);
  const nav = useNavigate();

  const load = () => api.get(`/admin/users?q=${encodeURIComponent(q)}`).then((r) => setUsers(r.data));

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  const toggle = async (u, field) => {
    try {
      await api.patch(`/admin/users/${u.id}`, { [field]: !u[field] });
      toast.success("Updated");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const del = async (u) => {
    if (!window.confirm(`Delete ${u.email}? This removes their attempts and progress.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <AdminLayout title="Users" actions={
      <>
        <Input placeholder="Search by name or email..." value={q} onChange={(e) => setQ(e.target.value)} className="w-72 bg-white/5 border-white/15 text-white" data-testid="user-search" />
        <Button onClick={() => setShowNew(true)} className="bg-[hsl(var(--accent))] text-black hover:bg-[hsl(var(--accent))]/90 shrink-0" data-testid="new-user-btn">
          <UserPlus className="w-4 h-4 mr-1" /> New user
        </Button>
      </>
    }>
      <div className="bg-white/4 border border-white/10 rounded-lg overflow-hidden">
        <table className="w-full text-sm" data-testid="users-table">
          <thead className="bg-white/5 text-xs uppercase tracking-widest text-white/50 font-mono">
            <tr>
              <th className="text-left px-4 py-3">User</th>
              <th className="text-left px-4 py-3">Exams</th>
              <th className="text-left px-4 py-3">Pass</th>
              <th className="text-left px-4 py-3">Answered</th>
              <th className="text-left px-4 py-3">Joined</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-white/8 hover:bg-white/5 cursor-pointer" data-testid={`user-row-${u.id}`}>
                <td className="px-4 py-3" onClick={() => nav(`/admin/users/${u.id}`)}>
                  <div className="font-medium">{u.name}</div>
                  <div className="text-xs text-white/50">{u.email}</div>
                </td>
                <td className="px-4 py-3 font-mono">{u.exams_taken}</td>
                <td className="px-4 py-3 font-mono text-emerald-400">{u.exams_passed}</td>
                <td className="px-4 py-3 font-mono">{u.questions_answered}</td>
                <td className="px-4 py-3 text-xs text-white/50 font-mono">{new Date(u.created_at).toLocaleDateString()}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-1">
                    {u.is_admin && <span className="text-[10px] font-mono uppercase px-1.5 py-0.5 rounded bg-[hsl(var(--accent))]/20 text-[hsl(var(--accent))]">Admin</span>}
                    {u.blocked && <span className="text-[10px] font-mono uppercase px-1.5 py-0.5 rounded bg-red-500/20 text-red-400">Blocked</span>}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-1">
                    <Button size="sm" variant="ghost" onClick={() => setResetUser(u)} title="Reset password" data-testid={`reset-password-${u.id}`}>
                      <KeyRound className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => toggle(u, "is_admin")} title={u.is_admin ? "Demote" : "Promote"} data-testid={`toggle-admin-${u.id}`}>
                      {u.is_admin ? <ShieldOff className="w-4 h-4" /> : <Shield className="w-4 h-4" />}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => toggle(u, "blocked")} title={u.blocked ? "Unblock" : "Block"} data-testid={`toggle-block-${u.id}`}>
                      {u.blocked ? <Check className="w-4 h-4" /> : <Ban className="w-4 h-4" />}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => del(u)} className="text-red-400 hover:text-red-300" data-testid={`delete-user-${u.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
            {users.length === 0 && (
              <tr><td colSpan={7} className="text-center py-10 text-white/40">No users found.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {showNew && <NewUserDialog onClose={() => setShowNew(false)} onCreated={load} />}
      {resetUser && <ResetPasswordDialog user={resetUser} onClose={() => setResetUser(null)} />}
    </AdminLayout>
  );
}
